import { useState, useEffect } from "react";
import { Outlet, Link } from "react-router-dom";
import Sidebar from "./Sidebar";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { LogOut, User, Menu, Sun, Moon, MessageCircle, X } from "lucide-react";
import api from "@/lib/api";
import { playNotificationSound } from "@/components/shared/ChatComponents";
import "@/styles/crm.css";

export default function CRMLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const prevUnreadRef = useState(0);

  // Poll for unread messages
  useEffect(() => {
    const check = () => {
      api.get("/chat/unread-count").then((r) => {
        const count = r.data.unread || 0;
        // Play sound when new unread messages arrive
        if (count > unread && unread >= 0) {
          playNotificationSound();
          setBannerDismissed(false);
        }
        setUnread(count);
      }).catch(() => {});
    };
    check();
    const iv = setInterval(check, 5000);
    return () => clearInterval(iv);
  }, [unread]);

  return (
    <div className="crm-page">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <main className="lg:ml-60 min-h-screen" data-testid="crm-main-content">
        {/* Unread messages banner */}
        {unread > 0 && !bannerDismissed && (
          <div data-testid="unread-banner" className="flex items-center justify-between px-4 sm:px-6 py-3"
            style={{ background: "linear-gradient(90deg, #dc2626, #ef4444)", color: "white" }}>
            <Link to="/crm/chat" className="flex items-center gap-3 flex-1" onClick={() => setBannerDismissed(true)}>
              <MessageCircle className="w-5 h-5 animate-pulse" />
              <span className="text-sm font-bold">
                {unread} {unread === 1 ? "nova mensagem" : "novas mensagens"} de clientes aguardando resposta!
              </span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/crm/chat" onClick={() => setBannerDismissed(true)}
                className="text-xs font-bold px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors">
                Responder
              </Link>
              <button onClick={() => setBannerDismissed(true)} className="p-1 rounded hover:bg-white/20 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div className="h-16 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30 backdrop-blur-md"
          style={{ background: "var(--crm-topbar-bg)", borderBottom: "1px solid var(--crm-border)" }}>
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"
            className="lg:hidden p-2 -ml-1 rounded-lg transition-colors" style={{ color: "var(--crm-text-muted)" }}>
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-4 ml-auto">
            {/* Chat indicator */}
            {unread > 0 && (
              <Link to="/crm/chat" data-testid="chat-indicator" className="relative p-2 rounded-lg transition-colors" style={{ color: "var(--crm-text-muted)" }}>
                <MessageCircle className="w-5 h-5" />
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                  style={{ background: "#ef4444", color: "white" }}>
                  {unread}
                </span>
              </Link>
            )}

            <button onClick={toggle} data-testid="theme-toggle"
              className="p-2 rounded-lg transition-all" style={{ color: "var(--crm-text-muted)", background: "var(--crm-surface)" }}
              title={theme === "dark" ? "Modo claro" : "Modo escuro"}>
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            <div className="flex items-center gap-2 text-sm" style={{ color: "var(--crm-text-secondary)" }}>
              <User className="w-4 h-4 hidden sm:block" strokeWidth={1.5} />
              <span data-testid="user-name" className="hidden sm:inline font-medium">{user?.name}</span>
              <span className="crm-badge crm-badge-orange text-[10px] uppercase">{user?.role}</span>
            </div>

            <button onClick={logout} data-testid="logout-button"
              className="flex items-center gap-1.5 text-sm transition-colors rounded-lg px-2 py-1.5" style={{ color: "var(--crm-text-dim)" }}>
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
